import { useEffect, useState } from "react";
import { Card, Col, Row, Table, Tag, Typography, Button, Select, Space, TimePicker, message } from "antd";
import { ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, SyncOutlined, StopOutlined } from "@ant-design/icons";
import moment from "moment";
import { AdminMenuLayout } from "../../../shared/components";
import { useSiesaMonitoring } from "../hooks/useSiesaMonitoring";

const { Title } = Typography;

const endpointOptions = [
  { label: "Todos", value: "" },
  { label: "Terceros", value: "/crear/terceros" },
  { label: "Clientes", value: "/crear/clientes" },
  { label: "Entidad terceros", value: "/crear/entidad-terceros" },
  { label: "Impuestos", value: "/crear/impuestos-masivo" },
  { label: "Pedidos", value: "/ventas/pedidos" },
  { label: "Recaudos", value: "/crear/recaudos" },
  { label: "Inventario", value: "/ventas/inventario-bodega-compania" },
];

export const SiesaMonitoring = () => {
  const {
    stats,
    logs,
    logsTotal,
    loading,
    logsPage,
    setLogsPage,
    logsFilter,
    setLogsFilter,
    cutoffTime,
    savingCutoff,
    saveCutoff,
    refresh,
  } = useSiesaMonitoring();
  const [cutoffDraft, setCutoffDraft] = useState(cutoffTime);

  useEffect(() => {
    setCutoffDraft(cutoffTime);
  }, [cutoffTime]);

  const cutoffMoment = moment(cutoffDraft, "HH:mm");

  const logsColumns = [
    {
      title: "Fecha",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 160,
      render: (val: string) => moment(val).format("DD/MM/YY HH:mm:ss"),
    },
    {
      title: "Endpoint",
      dataIndex: "endpoint",
      key: "endpoint",
      width: 200,
    },
    {
      title: "Método",
      dataIndex: "method",
      key: "method",
      width: 80,
    },
    {
      title: "Status",
      dataIndex: "success",
      key: "success",
      width: 80,
      render: (val: boolean) =>
        val ? (
          <Tag color="green">OK</Tag>
        ) : (
          <Tag color="red">Error</Tag>
        ),
    },
    {
      title: "HTTP",
      dataIndex: "status_code",
      key: "status_code",
      width: 60,
    },
    {
      title: "Duración",
      dataIndex: "duration_ms",
      key: "duration_ms",
      width: 90,
      render: (val: number) => (val ? `${val}ms` : "-"),
    },
    {
      title: "Error",
      dataIndex: "error_message",
      key: "error_message",
      ellipsis: true,
      render: (val: string) => val || "-",
    },
  ];

  const renderEntityCard = (
    title: string,
    data: { pending: number; synced: number; failed: number; not_required: number } | undefined
  ) => {
    if (!data) return null;
    const items = [
      { label: "Por enviar", value: data.pending, color: "#d48806", icon: <SyncOutlined /> },
      { label: "Enviados", value: data.synced, color: "#389e0d", icon: <CheckCircleOutlined /> },
      { label: "Con error", value: data.failed, color: "#cf1322", icon: <CloseCircleOutlined /> },
      { label: "No aplica", value: data.not_required, color: "#8c8c8c", icon: <StopOutlined /> },
    ];
    return (
      <Card title={title} size="small" bodyStyle={{ padding: "12px 16px" }}>
        <Row gutter={[12, 16]}>
          {items.map((item) => (
            <Col span={12} key={item.label}>
              <div style={{ color: "#8c8c8c", fontSize: 13, marginBottom: 2 }}>{item.label}</div>
              <div style={{ color: item.color, fontSize: 22, fontWeight: 600, lineHeight: 1.2 }}>
                <span style={{ marginRight: 8 }}>{item.icon}</span>
                {item.value}
              </div>
            </Col>
          ))}
        </Row>
      </Card>
    );
  };

  return (
    <AdminMenuLayout pageDescription="Monitoreo Siesa" title="Siesa">
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Monitoreo Siesa
          </Title>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={refresh}
          >
            Actualizar
          </Button>
        </Col>
      </Row>

      <Card style={{ marginBottom: 24 }} bodyStyle={{ padding: "20px 24px" }}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col flex="auto">
            <Title level={5} style={{ margin: 0 }}>
              Último día del mes
            </Title>
            <Typography.Paragraph type="secondary" style={{ margin: "6px 0 0", maxWidth: 560 }}>
              Los pedidos que entren después de esta hora no se envían a Siesa ese día.
              Se envían al día siguiente. El resto del mes se envían en cualquier hora.
            </Typography.Paragraph>
          </Col>
          <Col>
            <Space direction="vertical" size={4}>
              <Typography.Text strong>Hora límite</Typography.Text>
              <Space>
                <TimePicker
                  value={cutoffMoment.isValid() ? cutoffMoment : moment("19:00", "HH:mm")}
                  format="h:mm a"
                  use12Hours
                  allowClear={false}
                  disabled={savingCutoff}
                  onChange={(value) => {
                    if (value) setCutoffDraft(value.format("HH:mm"));
                  }}
                />
                <Button
                  type="primary"
                  loading={savingCutoff}
                  disabled={cutoffDraft === cutoffTime}
                  onClick={() => {
                    saveCutoff(cutoffDraft)
                      .then(() => message.success("Hora límite guardada"))
                      .catch((error) => {
                        message.error(error?.message || "No se pudo guardar la hora");
                      });
                  }}
                >
                  Guardar
                </Button>
              </Space>
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={8}>{renderEntityCard("Pedidos", stats?.orders)}</Col>
        <Col span={8}>{renderEntityCard("Usuarios", stats?.users)}</Col>
        <Col span={8}>{renderEntityCard("Pagos", stats?.payments)}</Col>
      </Row>

      <Card
        title="Logs de Integración"
        extra={
          <Space>
            <Select
              placeholder="Filtrar endpoint"
              style={{ width: 200 }}
              options={endpointOptions}
              value={logsFilter.endpoint || ""}
              onChange={(val) => setLogsFilter({ ...logsFilter, endpoint: val || undefined })}
            />
            <Select
              placeholder="Estado"
              style={{ width: 120 }}
              allowClear
              options={[
                { label: "Exitoso", value: true },
                { label: "Error", value: false },
              ]}
              value={logsFilter.success}
              onChange={(val) => setLogsFilter({ ...logsFilter, success: val })}
            />
          </Space>
        }
      >
        <Table
          dataSource={logs}
          columns={logsColumns}
          rowKey="id"
          size="small"
          pagination={{
            current: logsPage,
            pageSize: 20,
            total: logsTotal,
            onChange: setLogsPage,
            showTotal: (total) => `${total} registros`,
          }}
        />
      </Card>
    </AdminMenuLayout>
  );
};
